import { blue } from '@ant-design/colors';
import {
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  RocketTwoTone,
  SearchOutlined
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Checkbox,
  Popover,
  Slider,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { NoteElementSelection } from './App';
import { defaultDeviationPercentage } from './constants';
import usePatterns from './hooks/usePatterns';
import { discoveredOperators, Family, operatorOfRelation } from './models/Discovery';
import {
  getIconForOperator,
  Occurrence,
  Operator,
  OperatorsInOrder,
  Pattern
} from './models/Pattern';
import { SelectionContext } from './SelectionContext';

export const suggestionsID = 'suggestions';

const { CheckableTag } = Tag;
const { Title } = Typography;

const AlignedButton = styled(Button)`
  align-items: center;
  display: flex;
  justify-content: center;

  .ant-btn-loading-icon {
    display: flex;
  }
`;

const AlignedSpin = styled(Spin)`
  display: flex;

  .ant-spin {
    line-height: unset;
  }
`;

const CompactCheckableTag = styled(CheckableTag)`
  margin: 0 !important;
  margin-right: 0.25rem !important;
`;

const CompressedBadge = styled(Badge)`
  line-height: normal;
`;

/** A discovered family whose entries the panel shows by operator (App: the one clicked or selected). */
export interface SuggestionsFamily {
  color: string;
  family: Family;
  /** Operators whose entries are not highlighted. */
  hiddenOperators: Operator[];
  /** Whether the family is shown in the sheet at all. */
  isShown: boolean;
  /** Short label (T, CS1, ...) and name. */
  label: string;
  name: string;
  onHideOperators: (hiddenOperators: Operator[]) => void;
  onHoverOccurrences: (occurrences: Occurrence[]) => void;
  onShow: () => void;
}

/*
 * The classes of the states never compete for the same property: Tailwind 4 orders competing utilities differently
 * than Tailwind 3 (e.g. border-blue-500 before border-gray-300). This reproduces the Tailwind 3 result, in which a
 * hover:border-gray-300 of disabled images lost against hover:border-blue-500.
 */
const checkedStyles = 'border-blue-500 border-2';
const uncheckedStyles = 'border-gray-300';
const disabledStyles = 'cursor-not-allowed opacity-25';
const enabledStyles = 'cursor-pointer';

/**
 * The operators of a discovered family: how many of its entries each relates to the first entry (backend relations,
 * see operatorOfRelation), and which of them are highlighted in the sheet and the timeline.
 */
const FamilySuggestions = ({
  family: { color, family, hiddenOperators, isShown, label, name, ...on }
}: {
  family: SuggestionsFamily;
}) => {
  const { t: translate } = useTranslation();
  const entriesOf = (operator: Operator) =>
    family.occurrences.filter((entry) => operatorOfRelation(entry.relation) === operator);
  const asWritten = family.occurrences.filter(
    (entry) => operatorOfRelation(entry.relation) === undefined
  ).length;
  const related = family.occurrences.length - asWritten;
  const available = OperatorsInOrder.filter((operator) => entriesOf(operator).length > 0);
  const shown = available.filter((operator) => !hiddenOperators.includes(operator));
  const allShown = available.length > 0 && shown.length === available.length;

  const toggle = (operator: Operator) =>
    on.onHideOperators(
      hiddenOperators.includes(operator)
        ? hiddenOperators.filter((hidden) => hidden !== operator)
        : [...hiddenOperators, operator]
    );

  return (
    <div className="mb-5 mt-3 flex flex-col" data-family={family.label} id="suggestions-family">
      <div className="flex items-center gap-1.5">
        <span
          className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1 text-xs font-bold"
          style={{
            background: color,
            color: tinycolor.mostReadable(color, ['#ffffff', '#0b0b0b']).toHexString()
          }}
        >
          {label}
        </span>
        <span className="text-sm font-semibold text-slate-900">{name}</span>
      </div>
      <p className="m-0 mt-1 text-xs text-slate-600" id="suggestions-family-summary">
        {translate('suggestions.family.summary', {
          entries: translate('family.entries', { count: family.occurrences.length }),
          related,
          self: asWritten
        })}
      </p>
      {!isShown && (
        <p className="m-0 mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600">
          {translate('suggestions.family.hidden')}
          <Button onClick={on.onShow} size="small" type="link">
            {translate('suggestions.family.show')}
          </Button>
        </p>
      )}
      <div className="mb-4 mt-4 flex flex-col">
        <Tooltip placement="right" title={translate('suggestions.family.all')}>
          <Checkbox
            checked={allShown}
            className="!mb-2 !ml-1 self-start"
            disabled={available.length === 0}
            indeterminate={shown.length > 0 && !allShown}
            onChange={() =>
              on.onHideOperators(
                allShown
                  ? [...new Set([...hiddenOperators, ...available])]
                  : hiddenOperators.filter((hidden) => !available.includes(hidden))
              )
            }
          >
            <div className="flex items-center">
              {translate('suggestions.operators.all.title')}
              <Badge
                className="!ml-1.5"
                count={related}
                showZero
                size="small"
                style={{ backgroundColor: '#1890ff' }}
              />
            </div>
          </Checkbox>
        </Tooltip>
        {OperatorsInOrder.map((operator) => {
          const entries = entriesOf(operator);
          const isDisabled = entries.length === 0;
          const isChecked = !isDisabled && !hiddenOperators.includes(operator);
          const title = translate(`suggestions.operators.${operator}.title`);

          return (
            <Tooltip
              key={operator}
              placement="right"
              title={
                isDisabled
                  ? discoveredOperators.includes(operator)
                    ? translate('suggestions.family.none', { label })
                    : translate('suggestions.family.notSearched')
                  : translate('suggestions.family.toggle', { count: entries.length })
              }
            >
              <div
                className="grid mt-3"
                data-operator={operator}
                onMouseEnter={() => !isDisabled && on.onHoverOccurrences(entries)}
                onMouseLeave={() => !isDisabled && on.onHoverOccurrences([])}
                style={{ gridTemplateColumns: 'fit-content(20%) fit-content(80%)' }}
              >
                <CompressedBadge
                  className="!mr-2"
                  color="blue"
                  count={entries.length}
                  offset={[0, 1]}
                  size="small"
                >
                  <button
                    aria-checked={isChecked}
                    aria-disabled={isDisabled}
                    aria-label={title}
                    className="p-0"
                    onClick={() => !isDisabled && toggle(operator)}
                    role="checkbox"
                    style={{ background: 'transparent', border: 0, display: 'block' }}
                    type="button"
                  >
                    <img
                      alt={operator}
                      className={`block p-0.5 border border-solid rounded ${
                        isDisabled ? '' : 'hover:border-blue-500'
                      } ${isChecked ? checkedStyles : uncheckedStyles} ${
                        isDisabled ? disabledStyles : enabledStyles
                      }`}
                      height={24}
                      src={getIconForOperator(operator)}
                      width={24}
                    />
                  </button>
                </CompressedBadge>
                <div
                  className={`flex flex-col leading-none ${isDisabled ? 'opacity-50' : ''}`}
                  style={{ fontSize: 'small' }}
                >
                  <span className="font-bold">{title}</span>
                  <span className="mt-0.5">
                    {operator === Operator.DEVIATION
                      ? translate('suggestions.family.deviation')
                      : translate(`suggestions.operators.${operator}.description`)}
                  </span>
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
      <p className="m-0 text-xs text-slate-500">{translate('suggestions.family.searchHint')}</p>
    </div>
  );
};

const Suggestions = ({
  configuringID,
  family,
  hoveredPatternIDs,
  musicSheetID,
  newPattern,
  noteElements,
  patterns,
  setConfiguringID,
  setHoveredPatternIDs,
  setNewPattern,
  setPatterns,
  setSuggestingID,
  setVisibleOperatorsByDefault,
  suggestingID,
  visibleOperatorsByDefault
}: {
  configuringID: string;
  /** A discovered family to show the operators of, when no pattern of the analyst's own is configured. */
  family?: SuggestionsFamily;
  hoveredPatternIDs: string[];
  musicSheetID: string;
  newPattern: Pattern | undefined;
  noteElements: NoteElementSelection;
  patterns: Pattern[];
  setConfiguringID: Dispatch<SetStateAction<string>>;
  setHoveredPatternIDs: Dispatch<SetStateAction<string[]>>;
  setNewPattern: Dispatch<SetStateAction<Pattern | undefined>>;
  setPatterns: Dispatch<SetStateAction<Pattern[]>>;
  setSuggestingID: Dispatch<SetStateAction<string>>;
  setVisibleOperatorsByDefault: Dispatch<SetStateAction<Operator[]>>;
  suggestingID: string;
  visibleOperatorsByDefault: Operator[];
}) => {
  const [deviationPercentages, setDeviationPercentages] = useState<Map<string, number>>(new Map());
  const [isTonal, setIsTonal] = useState<boolean>(true);
  const { loadingStatus, updateDeviationOperator } = usePatterns(
    musicSheetID,
    noteElements,
    newPattern,
    deviationPercentages.get(configuringID) || defaultDeviationPercentage,
    isTonal,
    patterns,
    setNewPattern,
    setPatterns
  );
  const [selection] = useContext(SelectionContext);
  const [visibleOperators, setVisibleOperators] = useState<Map<string, Operator[]>>(new Map());
  const { t: translate } = useTranslation();

  const configuringPattern = patterns.find((pattern) => pattern.id === configuringID);
  const patternsConnected =
    patterns.find((pattern) => pattern.id === configuringID)?.connectedToByOperator.length || 0;
  // "All" means every operator that found related patterns (audit ISSUE-020: it compared with all eight).
  const availableOperators = Object.values(Operator).filter((operator) =>
    configuringPattern?.connectedToByOperator.some((target) => target.byOperator === operator)
  );
  const allAvailableVisible =
    availableOperators.length > 0 &&
    availableOperators.every((operator) =>
      (visibleOperators.get(configuringID) ?? []).includes(operator)
    );

  const makePatternVisible = () =>
    setPatterns((oldPatterns) => {
      const oldPattern = oldPatterns.find(({ id }) => id === configuringID);

      if (oldPattern) {
        oldPattern.connectedToByOperator.forEach((target) => {
          const targetPattern = oldPatterns.find(({ id }) => id === target.id);

          if (targetPattern) {
            targetPattern.isVisible = visibleOperators
              .get(configuringID)
              .contains(target.byOperator);
          }
        });
      }

      return [...oldPatterns];
    });

  useEffect(() => {
    patterns.forEach((pattern) => {
      if (!deviationPercentages.has(pattern.id)) {
        setDeviationPercentages((oldDeviationPercentages) =>
          new Map(oldDeviationPercentages).set(pattern.id, defaultDeviationPercentage)
        );
      }

      if (!visibleOperators.has(pattern.id)) {
        setVisibleOperators((oldVisibleOperators) =>
          new Map(oldVisibleOperators).set(pattern.id, visibleOperatorsByDefault)
        );
      }
    });
  }, [patterns]);

  useEffect(() => {
    if (configuringPattern) {
      setPatterns((oldPatterns) => {
        const oldPattern = oldPatterns.find(({ id }) => id === configuringID);

        if (oldPattern) {
          oldPattern.connectedToByOperator.forEach(({ id: connectedPatternID, byOperator }) => {
            const connectedPattern = oldPatterns.find(({ id }) => id === connectedPatternID);

            if (connectedPattern) {
              connectedPattern.isVisible = visibleOperators.get(configuringID).contains(byOperator);
            }
          });
        }

        return [...oldPatterns];
      });
    }
  }, [visibleOperators]);

  // A parameter of the operators (paper 7.2): tonal variants keep the scale steps, real ones the semitones. It applies
  // to the next search too, so it stays at hand while no pattern is selected.
  const tonalSwitch = (
    <div className="mt-3 flex items-center justify-center gap-1.5 text-xs">
      <Switch
        aria-label={translate('suggestions.tonal')}
        checked={isTonal}
        onChange={setIsTonal}
        size="small"
      />
      <span>{translate('suggestions.tonal')}</span>
      <Tooltip title={translate('suggestions.tonalTooltip')}>
        <QuestionCircleOutlined />
      </Tooltip>
    </div>
  );

  const title = (
    <Title className="flex items-center" level={5}>
      <RocketTwoTone className="mr-1" twoToneColor="orange" />
      {translate('suggestions.title')}
    </Title>
  );

  // A discovered family: its entries by operator. Neither a family nor an own pattern: a hint instead of a wall of
  // disabled operators.
  if (family && (!configuringPattern || configuringID === `family-${family.family.label}`))
    return (
      <div className="mb-5 mt-5 pr-2.5 flex flex-col" id={suggestionsID}>
        {title}
        <FamilySuggestions family={family} key={family.family.label} />
      </div>
    );
  if (!configuringPattern)
    return (
      <div className="mb-5 mt-5 pr-2.5 flex flex-col" id={suggestionsID}>
        {title}
        <p className="m-0 mt-3 text-sm text-slate-500" id="suggestions-hint">
          {translate('suggestions.hint')}
        </p>
        <div className="mt-6">{tonalSwitch}</div>
      </div>
    );

  return (
    <div className="mb-5 mt-5 pr-2.5 flex flex-col" id={suggestionsID}>
      {title}
      <div className="mb-8 mt-5 flex flex-col">
        <Checkbox
          checked={allAvailableVisible}
          className="!mb-2 !ml-1"
          disabled={
            !configuringPattern ||
            configuringPattern.connectedToByOperator.length === 0 ||
            Array.from(loadingStatus.values()).some((isLoading) => isLoading) ||
            selection.focusingID !== undefined
          }
          indeterminate={
            (visibleOperators.get(configuringID)?.length ?? 0) > 0 && !allAvailableVisible
          }
          onChange={() => {
            setVisibleOperators((previouslyVisibleOperators) => {
              previouslyVisibleOperators.set(
                configuringID,
                allAvailableVisible ? [] : availableOperators
              );

              return new Map(previouslyVisibleOperators);
            });
            makePatternVisible();
          }}
        >
          <div className="flex items-center">
            {translate('suggestions.operators.all.title')}
            <Badge
              className="!ml-1.5"
              count={patternsConnected}
              size="small"
              style={{ backgroundColor: '#1890ff' }}
            />
          </div>
        </Checkbox>
        {OperatorsInOrder.map((operator) => {
          const patternsConnectedByOperator =
            patterns
              .find((pattern) => pattern.id === configuringID)
              ?.connectedToByOperator.filter((source) => source.byOperator === operator).length ||
            0;

          const isAccepted = configuringID !== undefined && suggestingID === undefined;
          const isChecked = visibleOperators.get(configuringID)?.includes(operator);
          const isLoading = loadingStatus.has(operator) && loadingStatus.get(operator);
          const isDisabled =
            !configuringPattern ||
            patternsConnectedByOperator === 0 ||
            isLoading ||
            selection.focusingID !== undefined;

          return (
            <div
              className="grid mt-3"
              key={operator}
              onMouseEnter={() => {
                if (isDisabled) return;

                const hoveredPatternIDs = patterns
                  .find((pattern) => pattern.id === configuringID)
                  ?.connectedToByOperator.filter((target) => target.byOperator === operator)
                  .map((connection) => connection.id);

                setPatterns((oldPatterns) => {
                  oldPatterns.forEach((oldPattern) => {
                    if (hoveredPatternIDs.includes(oldPattern.id)) {
                      oldPattern.isVisible = true;
                    }
                  });

                  return [...oldPatterns];
                });
                setHoveredPatternIDs(hoveredPatternIDs);
              }}
              onMouseLeave={() => {
                if (isDisabled) return;

                setPatterns((oldPatterns) => {
                  oldPatterns.forEach((oldPattern) => {
                    if (hoveredPatternIDs.includes(oldPattern.id)) {
                      oldPattern.isVisible = isChecked;
                    }
                  });

                  setHoveredPatternIDs([]);

                  return [...oldPatterns];
                });
              }}
              style={{ gridTemplateColumns: 'fit-content(20%) fit-content(80%)' }}
            >
              <CompressedBadge
                className="!mr-2"
                color="blue"
                count={patternsConnectedByOperator}
                offset={[0, 1]}
                size="small"
              >
                <img
                  alt={operator}
                  className={`p-0.5 border border-solid hover:border-blue-500 rounded ${
                    isChecked ? checkedStyles : uncheckedStyles
                  } ${isDisabled ? disabledStyles : enabledStyles}`}
                  height={24}
                  onClick={() => {
                    if (isDisabled) return;

                    setPatterns((oldPatterns) => {
                      const oldPattern = oldPatterns.find(({ id }) => id === configuringID);

                      if (oldPattern) {
                        oldPattern.connectedToByOperator.forEach((target) => {
                          const targetPattern = oldPatterns.find(({ id }) => id === target.id);

                          if (targetPattern) targetPattern.isVisible = !isChecked;
                        });
                      }

                      return [...oldPatterns];
                    });
                    setVisibleOperators((previouslyVisibleOperators) => {
                      const newVisibleOperators = isChecked
                        ? previouslyVisibleOperators
                            .get(configuringID)
                            .filter((visibleOperator) => visibleOperator !== operator)
                        : previouslyVisibleOperators.get(configuringID).concat(operator);

                      previouslyVisibleOperators.set(configuringID, newVisibleOperators);

                      return new Map(previouslyVisibleOperators);
                    });
                  }}
                  src={getIconForOperator(operator)}
                  width={24}
                />
              </CompressedBadge>
              <div className="flex flex-col leading-none" style={{ fontSize: 'small' }}>
                <div className="flex items-center">
                  <span className="font-bold">
                    {translate(`suggestions.operators.${operator}.title`)}
                  </span>
                  {isLoading && (
                    <AlignedSpin
                      className="!ml-1"
                      indicator={<LoadingOutlined style={{ fontSize: 'small' }} spin />}
                    />
                  )}
                </div>
                <span className="mt-0.5">
                  {operator === Operator.DEVIATION ? (
                    <Trans
                      components={[
                        <ChangeableDeviationPercentage
                          deviationPercentage={
                            deviationPercentages.get(configuringID) || defaultDeviationPercentage
                          }
                          isDisabled={isDisabled || isAccepted}
                          setDeviationPercentage={(newDeviationPercentage: number) => {
                            setDeviationPercentages((oldDeviationPercentages) =>
                              new Map(oldDeviationPercentages).set(
                                configuringID,
                                newDeviationPercentage
                              )
                            );
                            updateDeviationOperator(configuringID, newDeviationPercentage);
                          }}
                        />
                      ]}
                      i18nKey={`suggestions.operators.${operator}.description`}
                      values={{ deviationPercentage: deviationPercentages }}
                    ></Trans>
                  ) : (
                    translate(`suggestions.operators.${operator}.description`)
                  )}
                </span>
              </div>
            </div>
          );
        })}
        <div className="mt-6 flex items-center justify-center text-xs">
          <CompactCheckableTag
            checked={isEqual(visibleOperatorsByDefault, visibleOperators.get(configuringID))}
            onChange={(checked) =>
              checked && setVisibleOperatorsByDefault(visibleOperators.get(configuringID))
            }
          >
            {translate('suggestions.useAsDefault')}
          </CompactCheckableTag>
          <Tooltip title={translate('suggestions.useAsDefaultTooltip')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
        {tonalSwitch}
      </div>
      {suggestingID !== undefined && configuringPattern?.isUserDefined && (
        // Sticky, so the decision stays in view however long the list of operators is (audit ISSUE-004).
        <div className="sticky bottom-0 z-[1] flex flex-col bg-white pb-2 pt-2">
          <AlignedButton
            className="mb-1 self-center"
            icon={<CheckOutlined />}
            loading={Array.from(loadingStatus.values()).some((isLoading) => isLoading)}
            onClick={() => {
              makePatternVisible();
              setConfiguringID(undefined);
              setNewPattern(undefined);
              setSuggestingID(undefined);
            }}
            size="small"
            type="primary"
          >
            {translate('suggestions.accept')}
          </AlignedButton>
          <AlignedButton
            className="self-center"
            danger
            icon={<CloseOutlined />}
            loading={Array.from(loadingStatus.values()).some((isLoading) => isLoading)}
            onClick={() => {
              setPatterns((oldPatterns) => {
                const oldPattern = oldPatterns.find(({ id }) => id === suggestingID);
                const oldPatternIndex = oldPatterns.findIndex(({ id }) => id === suggestingID);

                if (oldPattern && oldPatternIndex !== -1) {
                  oldPattern.connectedToByOperator.forEach(({ id: connectedID }) => {
                    const oldConnectedPatternIndex = oldPatterns.findIndex(
                      ({ id }) => id === connectedID
                    );

                    if (oldConnectedPatternIndex !== -1)
                      oldPatterns.splice(oldConnectedPatternIndex, 1);
                  });
                  oldPatterns.splice(oldPatternIndex, 1);
                }

                return [...oldPatterns];
              });
              setVisibleOperators((previouslyVisibleOperators) => {
                previouslyVisibleOperators.delete(suggestingID);

                return new Map(previouslyVisibleOperators);
              });
              setConfiguringID(undefined);
              setNewPattern(undefined);
              setSuggestingID(undefined);
            }}
            size="small"
            type="primary"
          >
            {translate('suggestions.decline')}
          </AlignedButton>
        </div>
      )}
      {suggestingID === undefined &&
        configuringID !== undefined &&
        !configuringPattern?.isUserDefined && (
          <>
            <AlignedButton
              className="self-center"
              disabled={configuringPattern.hasBeenQueried}
              icon={<SearchOutlined />}
              onClick={() => setNewPattern(configuringPattern)}
              size="small"
              type="primary"
            >
              {translate('suggestions.generate')}
            </AlignedButton>
          </>
        )}
    </div>
  );
};

const ChangeableDeviationPercentage = ({
  deviationPercentage,
  isDisabled,
  setDeviationPercentage
}: {
  deviationPercentage: number;
  isDisabled: boolean;
  setDeviationPercentage: (deviationPercentage: number) => void;
}) => {
  const maximum = 50;
  const minimum = 5;

  const { t: translate } = useTranslation();

  return (
    <Popover
      content={() => (
        <div>
          <div className="flex items-center">
            <span className="flex-1 mr-1 text-gray-500 text-xs whitespace-nowrap">
              <i>{minimum} %</i>
            </span>
            <Slider
              autoFocus
              className="w-[90%]"
              defaultValue={deviationPercentage}
              disabled={isDisabled}
              max={maximum}
              min={minimum}
              onChangeComplete={setDeviationPercentage}
            />
            <span className="flex-1 ml-1 text-gray-500 text-xs whitespace-nowrap">
              <i>{maximum} %</i>
            </span>
          </div>
          <span className="block pb-1.5 text-gray-500 leading-none" style={{ fontSize: 11 }}>
            {translate('suggestions.operators.deviation.disclaimer')}
          </span>
        </div>
      )}
      id="deviation-percentage-slider"
      styles={{ container: { padding: '0 0.5rem' }, content: { padding: 0 }, root: { width: 250 } }}
      trigger="click"
    >
      {/* A button, so the threshold can be changed with the keyboard too (audit ISSUE-022). */}
      <button
        aria-label={translate('suggestions.deviationThreshold', {
          percentage: deviationPercentage
        })}
        className="cursor-pointer p-0 font-bold"
        style={{ background: 'transparent', border: 0, color: blue.primary, font: 'inherit' }}
        type="button"
      >
        {deviationPercentage} %
      </button>
    </Popover>
  );
};

export default Suggestions;
